import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Lock, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { GoogleIcon } from "@/components/GoogleIcon";

/**
 * Shown after the trial cap is exhausted OR when the user manually triggers
 * "save and continue". Keeps the current draft via draftStore so signup
 * doesn't lose work.
 */
export function SignupPromptModal({ open, onOpenChange, generationsUsed = 3 }) {
  const [loading, setLoading] = useState(false);

  const signInGoogle = async () => {
    setLoading(true);
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback?next=/tools/reply-assistant` },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="signup-prompt-modal"
        className="max-w-md rounded-2xl border-border bg-card p-0 shadow-2xl"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-7 pb-7 pt-9">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
            <Lock className="h-[18px] w-[18px]" />
          </div>
          <DialogTitle className="mt-4 font-display text-[22px] font-semibold tracking-tight text-foreground">
            You&apos;ve used your {generationsUsed} free replies.
          </DialogTitle>
          <DialogDescription className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
            Create a free account to keep going — your current draft and reply
            are saved and waiting for you.
          </DialogDescription>

          <div className="mt-5 rounded-xl border border-border bg-background p-3 text-[12.5px] text-muted-foreground">
            <p className="flex items-center gap-2 text-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              With a free account you get
            </p>
            <ul className="mt-2 space-y-1.5 pl-5">
              <li className="list-disc">10 AI generations every month</li>
              <li className="list-disc">Conversation history across devices</li>
              <li className="list-disc">All future tools (Proposals, Follow-ups…)</li>
            </ul>
          </div>

          <Button
            data-testid="signup-modal-google-btn"
            onClick={signInGoogle}
            disabled={loading}
            variant="outline"
            className="mt-6 h-11 w-full justify-center gap-2.5 rounded-lg border-border text-[13.5px] font-medium"
          >
            <GoogleIcon className="h-[18px] w-[18px]" /> Continue with Google
          </Button>

          <Button
            asChild
            data-testid="signup-modal-email-btn"
            className="mt-2 h-11 w-full rounded-lg bg-primary text-[13.5px] font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Link to="/signup?next=/tools/reply-assistant">Sign up with email</Link>
          </Button>

          <p className="mt-4 text-center text-[12px] text-muted-foreground">
            Already have an account?{" "}
            <Link
              to="/login?next=/tools/reply-assistant"
              className="text-foreground hover:text-primary"
            >
              Sign in
            </Link>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
